import React, { useState, useEffect } from "react";
import { Download, Send, Receipt, CheckCircle2, Clock, CheckSquare, Filter, AlertTriangle, Mail, Loader2, Trash2, XCircle, Bell, LayoutTemplate, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { EmptyState, openOnboarding } from "../../components/onboarding/EmptyState";
import { EMPTY as EMPTY_TXT, HELP } from "../../content/onboardingTexts";
import { HelpTip } from "../../components/onboarding/LaterSection";
import api from "../../lib/api";
import { useCachedList } from "../../lib/useCachedList";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FR_MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
// Parse un libellé "Juillet 2025" → Date (1er du mois). Le loyer est dû en début de période.
const monthToDate = (label) => {
  if (!label) return null;
  const parts = String(label).trim().toLowerCase().split(/\s+/);
  if (parts.length < 2) return null;
  const mi = FR_MONTHS.indexOf(parts[0]);
  const y = parseInt(parts[1], 10);
  if (mi < 0 || isNaN(y)) return null;
  return new Date(y, mi, 1);
};
const daysLate = (r) => {
  if (r.status === "Payé") return 0;
  const d = monthToDate(r.month);
  if (!d) return 0;
  const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
  return diff > 0 ? diff : 0;
};

export default function Quittances() {
  const navigate = useNavigate();
  const [items, setItems, itemsLoaded, load] = useCachedList("/receipts");
  const [arrearsDays, setArrearsDays] = useState(7);
  const [autoEscalate, setAutoEscalate] = useState(false);
  const [escalateDays, setEscalateDays] = useState(15);
  const [selected, setSelected] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [fBien, setFBien] = useState("");
  const [fTenant, setFTenant] = useState("");
  const [fMonth, setFMonth] = useState("");
  const [monthAutoset, setMonthAutoset] = useState(false);
  const [fStatus, setFStatus] = useState("");
  const [sendBusy, setSendBusy] = useState(false);
  const [offerSend, setOfferSend] = useState(0);
  const [confirmDel, setConfirmDel] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [properties, setProperties] = useState([]);
  const [relanceReceipt, setRelanceReceipt] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => { setArrearsDays(r.data.arrears_days); setAutoEscalate(!!r.data.auto_escalate); setEscalateDays(r.data.escalate_days || 15); }).catch(() => {});
    api.get("/properties").then((r) => setProperties(r.data || [])).catch(() => {});
  }, []);

  const biens = [...new Set(items.map((r) => r.property).filter(Boolean))];
  const tenantsList = [...new Set(items.map((r) => r.tenant).filter(Boolean))];
  const months = [...new Set(items.map((r) => r.month).filter(Boolean))];
  const statuses = [...new Set(items.map((r) => r.status).filter(Boolean))];
  useEffect(() => {
    if (monthAutoset || items.length === 0) return;
    const now = new Date();
    const cur = months.find((m) => { const d = monthToDate(m); return d && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth(); });
    if (cur) setFMonth(cur);
    setMonthAutoset(true);
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps
  const filtered = items.filter((r) => {
    if (fBien && r.property !== fBien) return false;
    if (fTenant && r.tenant !== fTenant) return false;
    if (fMonth && r.month !== fMonth) return false;
    if (fStatus && r.status !== fStatus) return false;
    return true;
  });

  const pending = filtered.filter((r) => r.status !== "Payé");
  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allPendingSelected = pending.length > 0 && pending.every((r) => selected.includes(r.id));
  const toggleSelAll = () => setSelected(allPendingSelected ? [] : pending.map((r) => r.id));

  const bulkPay = async () => {
    if (!selected.length) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/receipts/bulk-pay", { ids: selected });
      toast.success(`${res.data.paid} quittance(s) marquée(s) comme payée(s)`);
      setOfferSend(res.data.paid || 0);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la validation en masse");
    } finally { setBulkBusy(false); }
  };

  const bulkSend = async () => {
    setSendBusy(true);
    try {
      const res = await api.post("/receipts/bulk-send", { ids: [] });
      const { sent, no_email, failed } = res.data;
      if (sent > 0) toast.success(`${sent} quittance(s) envoyée(s)${no_email ? ` · ${no_email} sans email` : ""}${failed ? ` · ${failed} échec(s)` : ""}`);
      else toast.error(no_email ? `Aucun envoi : ${no_email} locataire(s) sans email` : "Aucune quittance envoyée");
      setOfferSend(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally { setSendBusy(false); }
  };

  // Toutes les cases proposées sont sur des quittances "En attente" → suppression en masse autorisée uniquement dans ce cas.
  const selectedItems = items.filter((r) => selected.includes(r.id));
  const allSelectedPending = selectedItems.length > 0 && selectedItems.every((r) => r.status === "En attente");
  const bulkDelete = async () => {
    if (!selected.length || !allSelectedPending) return;
    setDelBusy(true);
    try {
      const res = await api.post("/receipts/bulk-delete", { ids: selected });
      const ids = new Set(selected);
      setItems((p) => p.filter((r) => !ids.has(r.id)));
      toast.success(`${res.data.deleted} quittance(s) supprimée(s)`);
      setSelected([]);
      setConfirmDel(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    } finally { setDelBusy(false); }
  };

  const saveArrears = async () => {
    try {
      const res = await api.put("/settings", { arrears_days: Number(arrearsDays) || 7, auto_escalate: autoEscalate, escalate_days: Number(escalateDays) || 15 });
      setArrearsDays(res.data.arrears_days);
      setAutoEscalate(!!res.data.auto_escalate);
      setEscalateDays(res.data.escalate_days);
      toast.success(res.data.auto_escalate ? `Rappel auto à ${res.data.arrears_days} j · escalade tous les ${res.data.escalate_days} j` : `Rappel automatique réglé à ${res.data.arrears_days} jours`);
    } catch { toast.error("Erreur"); }
  };

  const markPaid = async (id) => {
    try {
      const res = await api.put(`/receipts/${id}/pay`);
      setItems(items.map((r) => (r.id === id ? res.data : r)));
      const a = res.data.auto_send;
      if (a && !a.error) {
        toast.success(a.simulated ? `Payé — quittance envoyée à ${a.to} (email simulé)` : `Payé — quittance envoyée à ${a.to}`);
      } else if (a && a.error === "no_email") {
        toast.success("Marqué payé (aucun email locataire pour l'envoi auto)");
      } else {
        toast.success("Quittance marquée comme payée");
      }
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const markUnpaid = async (id) => {
    try {
      const res = await api.put(`/receipts/${id}/unpaid`);
      setItems(items.map((r) => (r.id === id ? res.data : r)));
      toast.success("Quittance marquée « Non payé » — écriture comptable créée dans Finances");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const bulkUnpaid = async () => {
    if (!selected.length) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/receipts/bulk-unpaid", { ids: selected });
      toast.success(`${res.data.marked} quittance(s) marquée(s) « Non payé »`);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du marquage en masse");
    } finally { setBulkBusy(false); }
  };

  const sendRelance = async (r, level) => {
    try {
      const res = await api.post(`/receipts/${r.id}/arrears-reminder`, { level });
      const lvlName = level === 3 ? "Mise en demeure" : level === 2 ? "2e relance" : "1er rappel";
      if (res.data.simulated) toast.success(`${lvlName} envoyée à ${res.data.to} (email simulé — ajoutez une clé SendGrid pour l'envoi réel)`);
      else if (res.data.sent) toast.success(`${lvlName} envoyée à ${res.data.to}`);
      else toast.error("L'envoi a échoué (vérifiez la configuration SendGrid)");
      setRelanceReceipt(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
      throw e;
    }
  };

  const downloadPdf = (r) => {
    if (r.status !== "Payé") { toast.error("La quittance ne peut être émise que pour un loyer payé"); return; }
    window.open(`${BACKEND_URL}/api/receipts/${r.id}/pdf`, "_blank");
  };

  const sendPdf = async (r) => {
    if (r.status !== "Payé") { toast.error("Marquez d'abord le loyer comme payé"); return; }
    try {
      const res = await api.post(`/receipts/${r.id}/send`);
      if (res.data.simulated) toast.success(`Quittance ${r.month} envoyée à ${res.data.to} (email simulé — ajoutez une clé SendGrid pour l'envoi réel)`);
      else if (res.data.sent) toast.success(`Quittance ${r.month} envoyée à ${res.data.to}`);
      else toast.error("L'envoi a échoué (vérifiez la configuration SendGrid)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi");
    }
  };

  const generate = async (params = {}) => {
    try {
      const res = await api.post("/receipts/generate", { from_month: params.from_month || "", to_month: params.to_month || "", property: params.property || "" });
      const created = res.data.created || [];
      const skipped = res.data.skipped || 0;
      if (created.length > 0) setItems([...created, ...items]);
      if (created.length > 0) toast.success(`${created.length} quittance(s) générée(s)${skipped ? ` · ${skipped} déjà existante(s) ignorée(s)` : ""}`);
      else toast.info(skipped ? `Aucune nouvelle quittance : ${skipped} déjà générée(s) pour ${res.data.month || "cette période"}` : "Aucune quittance à générer");
      setGenOpen(false);
    } catch (e) {
      toast.error("Erreur lors de la génération");
    }
  };

  const total = items.filter((r) => r.status === "Payé").reduce((s, r) => s + r.amount, 0);
  const overdueCount = items.filter((r) => daysLate(r) > 0).length;

  if (itemsLoaded && items.length === 0) return <EmptyState icon={FileText} title={EMPTY_TXT.quittances.title} cta={EMPTY_TXT.quittances.cta} onCta={() => (properties.length ? navigate("/app/locations") : openOnboarding(1))} testid="empty-quittances" />;
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-brand-soft">
          <p className="text-sm text-brand-green/50">Total encaissé</p>
          <p className="text-2xl font-bold text-brand-greenDeep mt-1">{total.toLocaleString("fr-FR")} €</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-brand-soft">
          <p className="text-sm text-brand-green/50">Quittances émises</p>
          <p className="text-2xl font-bold text-brand-greenDeep mt-1">{items.length}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-brand-soft">
          <p className="text-sm text-brand-green/50">En attente</p>
          <p className="text-2xl font-bold text-orange-500 mt-1">{items.filter((r) => r.status !== "Payé").length}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-brand-soft" data-testid="overdue-card">
          <p className="text-sm text-brand-green/50">En retard</p>
          <p className={`text-2xl font-bold mt-1 ${overdueCount > 0 ? "text-red-600" : "text-brand-green/40"}`}>{overdueCount}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-5 border border-brand-soft flex flex-col gap-4" data-testid="arrears-settings">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0"><Clock className="w-5 h-5 text-amber-500" /></span>
            <div>
              <p className="font-medium text-brand-greenDeep text-sm">Rappel automatique de loyer impayé</p>
              <p className="text-xs text-brand-green/50">Un rappel est envoyé au locataire lorsqu'une quittance reste impayée au-delà du délai.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="number" min="1" max="90" value={arrearsDays} onChange={(e) => setArrearsDays(e.target.value)} className="w-20 px-3 py-2 rounded-xl border border-brand-soft outline-none text-sm focus:border-brand-limeDark" data-testid="arrears-days-input" />
            <span className="text-sm text-brand-green/60">jours</span>
            <button onClick={saveArrears} className="px-4 py-2 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm transition-colors" data-testid="save-arrears-btn">Enregistrer</button>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between border-t border-brand-soft pt-4">
          <button type="button" onClick={() => setAutoEscalate((v) => !v)} className="flex items-center gap-3 text-left" data-testid="toggle-escalate">
            <span className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ${autoEscalate ? "bg-brand-lime" : "bg-brand-soft"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${autoEscalate ? "left-[22px]" : "left-0.5"}`} />
            </span>
            <div>
              <p className="font-medium text-brand-greenDeep text-sm">Relance progressive automatique</p>
              <p className="text-xs text-brand-green/50">Passe automatiquement au niveau supérieur (1er rappel → 2e relance → mise en demeure).</p>
            </div>
          </button>
          <div className={`flex items-center gap-2 ${autoEscalate ? "" : "opacity-40 pointer-events-none"}`}>
            <span className="text-sm text-brand-green/60">Escalade tous les</span>
            <input type="number" min="1" max="90" value={escalateDays} onChange={(e) => setEscalateDays(e.target.value)} className="w-20 px-3 py-2 rounded-xl border border-brand-soft outline-none text-sm focus:border-brand-limeDark" data-testid="escalate-days-input" />
            <span className="text-sm text-brand-green/60">jours</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-brand-soft overflow-hidden">
        <div className="px-6 py-4 border-b border-brand-soft flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-semibold text-brand-greenDeep">Historique des quittances</h3>
            <div className="flex items-center gap-2 flex-wrap">
              {selected.length > 0 && (
                <button onClick={bulkPay} disabled={bulkBusy} data-testid="bulk-pay-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold text-sm transition-colors disabled:opacity-50">
                  {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckSquare className="w-4 h-4" />} Valider la sélection ({selected.length})
                </button>
              )}
              {selected.length > 0 && (
                <button onClick={bulkUnpaid} disabled={bulkBusy} data-testid="bulk-unpaid-btn" title="Marquer les quittances sélectionnées comme non payées (écriture comptable créée)" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 hover:bg-red-100 text-red-600 font-semibold text-sm transition-colors disabled:opacity-50">
                  {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Marquer non payé ({selected.length})
                </button>
              )}
              {selected.length > 0 && (
                <button onClick={() => setConfirmDel(true)} disabled={!allSelectedPending} data-testid="bulk-delete-btn" title={allSelectedPending ? "Supprimer les quittances en attente sélectionnées" : "Suppression possible uniquement pour des quittances en attente"} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 hover:bg-red-100 text-red-600 font-semibold text-sm transition-colors disabled:opacity-40">
                  <Trash2 className="w-4 h-4" /> Supprimer ({selected.length})
                </button>
              )}
              <button onClick={bulkSend} disabled={sendBusy} data-testid="bulk-send-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold text-sm transition-colors disabled:opacity-50" title="Envoyer toutes les quittances payées aux locataires">
                {sendBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Envoyer les payées
              </button>
              <button onClick={() => navigate("/app/modeles-documents")} data-testid="edit-receipt-template-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold text-sm transition-colors" title="Personnaliser la mise en page des quittances et factures">
                <LayoutTemplate className="w-4 h-4" /> Modèle
              </button>
              <button onClick={() => setGenOpen(true)} data-testid="generate-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm transition-colors">
                <Receipt className="w-4 h-4" /> Générer
              </button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="receipt-filters">
            <span className="text-brand-green/40"><Filter className="w-4 h-4" /></span>
            <select value={fBien} onChange={(e) => setFBien(e.target.value)} className="qsel" data-testid="filter-bien">
              <option value="">Tous les biens</option>
              {biens.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <select value={fTenant} onChange={(e) => setFTenant(e.target.value)} className="qsel" data-testid="filter-tenant">
              <option value="">Tous les locataires</option>
              {tenantsList.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={fMonth} onChange={(e) => setFMonth(e.target.value)} className="qsel" data-testid="filter-month">
              <option value="">Toutes les périodes</option>
              {months.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="qsel" data-testid="filter-status">
              <option value="">Tous les statuts</option>
              {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {(fBien || fTenant || fMonth || fStatus) && <button onClick={() => { setFBien(""); setFTenant(""); setFMonth(""); setFStatus(""); }} className="text-xs text-brand-green/50 hover:text-brand-greenDeep" data-testid="filter-clear">Réinitialiser</button>}
            <span className="text-xs text-brand-green/40 ml-auto">{filtered.length} quittance(s)</span>
          </div>
        </div>
        {offerSend > 0 && (
          <div className="px-6 py-3 bg-brand-lime/15 border-b border-brand-soft flex items-center justify-between gap-3 flex-wrap" data-testid="offer-send-banner">
            <p className="text-sm text-brand-greenDeep flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-limeDark" /> {offerSend} quittance(s) validée(s). Envoyer les quittances payées aux locataires ?</p>
            <div className="flex items-center gap-2">
              <button onClick={() => setOfferSend(0)} className="text-xs text-brand-green/50 hover:text-brand-greenDeep px-3 py-1.5" data-testid="offer-send-dismiss">Plus tard</button>
              <button onClick={bulkSend} disabled={sendBusy} data-testid="offer-send-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold text-sm transition-colors disabled:opacity-50">
                {sendBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Envoyer maintenant
              </button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-auto">
            <thead>
              <tr className="text-left text-brand-green/50 border-b border-brand-soft">
                <th className="px-6 py-3 font-medium w-10">
                  <input type="checkbox" checked={allPendingSelected} onChange={toggleSelAll} disabled={pending.length === 0} data-testid="select-all-receipts" className="w-4 h-4 accent-brand-limeDark cursor-pointer disabled:opacity-40" title="Tout sélectionner (en attente)" />
                </th>
                <th className="px-6 py-3 font-medium whitespace-nowrap">Locataire</th>
                <th className="px-6 py-3 font-medium hidden md:table-cell whitespace-nowrap">Bien</th>
                <th className="px-6 py-3 font-medium whitespace-nowrap">Période</th>
                <th className="px-6 py-3 font-medium whitespace-nowrap">Montant</th>
                <th className="px-6 py-3 font-medium whitespace-nowrap">Statut</th>
                <th className="px-6 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan="7" className="px-6 py-8 text-center text-brand-green/50" data-testid="receipts-empty">{itemsLoaded ? "Aucune quittance." : <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement des quittances…</span>}</td></tr>}
              {filtered.map((r) => {
                const late = daysLate(r);
                return (
                <tr key={r.id} className="border-b border-brand-soft last:border-0 hover:bg-brand-softer/50">
                  <td className="px-6 py-4">
                    {r.status !== "Payé" && (
                      <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleSel(r.id)} data-testid={`select-receipt-${r.id}`} className="w-4 h-4 accent-brand-limeDark cursor-pointer" />
                    )}
                  </td>
                  <td className="px-6 py-4 font-medium text-brand-greenDeep whitespace-nowrap">{r.tenant}</td>
                  <td className="px-6 py-4 text-brand-green/60 hidden md:table-cell whitespace-nowrap">{r.property}</td>
                  <td className="px-6 py-4 text-brand-green/60 whitespace-nowrap">{r.month}</td>
                  <td className="px-6 py-4 font-semibold text-brand-greenDeep whitespace-nowrap">{r.amount.toLocaleString("fr-FR")} €</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {r.status === "Payé" ? (
                      <span className="inline-flex items-center gap-1.5 text-brand-limeDark font-medium"><CheckCircle2 className="w-4 h-4" /> Payé</span>
                    ) : r.status === "Non payé" ? (
                      <span className="inline-flex items-center gap-1.5 text-red-600 font-medium" data-testid={`status-unpaid-${r.id}`}><XCircle className="w-4 h-4" /> Non payé</span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 text-orange-500 font-medium"><Clock className="w-4 h-4" /> En attente</span>
                        {late > 0 && <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold" data-testid={`overdue-badge-${r.id}`}><AlertTriangle className="w-3 h-3" /> {late} j de retard</span>}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {r.status !== "Payé" && (
                        <button onClick={() => markPaid(r.id)} data-testid={`mark-paid-${r.id}`} className="px-3 py-1.5 rounded-full bg-brand-lime text-brand-greenDeep text-xs font-semibold hover:bg-brand-limeDark transition-colors whitespace-nowrap">Marquer payé</button>
                      )}
                      {r.status === "En attente" && (
                        <button onClick={() => markUnpaid(r.id)} data-testid={`mark-unpaid-${r.id}`} title="Créer une écriture comptable « Non payé » dans Finances" className="px-3 py-1.5 rounded-full bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-100 transition-colors whitespace-nowrap">Marquer non payé</button>
                      )}
                      {r.status === "Non payé" && (
                        <button onClick={() => setRelanceReceipt(r)} data-testid={`relance-btn-${r.id}`} title="Envoyer un courrier de relance de loyer impayé au locataire" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold hover:bg-amber-100 transition-colors whitespace-nowrap"><Bell className="w-3.5 h-3.5" /> Relance</button>
                      )}
                      <button onClick={() => downloadPdf(r)} disabled={r.status !== "Payé"} title="Télécharger la quittance PDF" data-testid={`receipt-pdf-${r.id}`} className="w-8 h-8 rounded-full border border-brand-soft flex items-center justify-center text-brand-green hover:bg-brand-softer transition-colors disabled:opacity-40"><Download className="w-4 h-4" /></button>
                      <button onClick={() => sendPdf(r)} disabled={r.status !== "Payé"} title="Envoyer au locataire" data-testid={`receipt-send-${r.id}`} className="w-8 h-8 rounded-full border border-brand-soft flex items-center justify-center text-brand-green hover:bg-brand-softer transition-colors disabled:opacity-40"><Send className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <style>{`.qsel{padding:0.4rem 0.7rem;border-radius:0.7rem;border:1px solid #efe7d6;outline:none;background:#fff;font-size:0.8rem;max-width:200px}.qsel:focus{border-color:#c9a24b}`}</style>
      {confirmDel && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={() => !delBusy && setConfirmDel(false)}>          <div className="bg-white rounded-3xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="receipt-bulk-delete-modal">
            <div className="p-6 pb-4 border-b border-brand-soft flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-red-50 flex items-center justify-center shrink-0"><Trash2 className="w-5 h-5 text-red-500" /></span>
              <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Supprimer {selected.length} quittance(s) ?</h3>
            </div>
            <div className="p-6 pt-5 space-y-4">
              <p className="text-sm text-brand-green/70">Cette action est définitive. Seules les quittances <b>en attente</b> peuvent être supprimées en masse.</p>
              <div className="flex gap-3">
                <button onClick={() => setConfirmDel(false)} disabled={delBusy} className="flex-1 py-3 rounded-full border border-brand-soft text-brand-greenDeep font-semibold hover:bg-brand-softer transition-colors disabled:opacity-60" data-testid="receipt-bulk-delete-cancel">Annuler</button>
                <button onClick={bulkDelete} disabled={delBusy || !allSelectedPending} className="flex-1 py-3 rounded-full bg-red-500 hover:bg-red-600 text-white font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60" data-testid="receipt-bulk-delete-confirm">
                  {delBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {genOpen && <GenerateModal onClose={() => setGenOpen(false)} onGenerate={generate} properties={properties} />}
      {relanceReceipt && <RelanceModal receipt={relanceReceipt} onClose={() => setRelanceReceipt(null)} onSend={sendRelance} />}
    </div>
  );
}

function GenerateModal({ onClose, onGenerate, properties = [] }) {
  const now = new Date();
  const curYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [mode, setMode] = useState("single"); // "single" | "range"
  const [from, setFrom] = useState(curYm);
  const [to, setTo] = useState(curYm);
  const [property, setProperty] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!from) return toast.error("Choisissez un mois");
    if (mode === "range" && !to) return toast.error("Choisissez le mois de fin");
    setBusy(true);
    try {
      const base = mode === "single" ? { from_month: from } : { from_month: from, to_month: to };
      await onGenerate({ ...base, property });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="bg-white rounded-3xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="generate-modal">
        <div className="p-6 pb-4 border-b border-brand-soft flex items-center gap-3">
          <span className="w-11 h-11 rounded-full bg-brand-lime/30 flex items-center justify-center shrink-0"><Receipt className="w-5 h-5 text-brand-greenDeep" /></span>
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Générer des quittances<HelpTip text={HELP.quittance} /></h3>
        </div>
        <div className="p-6 pt-5 space-y-4">
          <p className="text-sm text-brand-green/60">Génère les quittances pour la période choisie. Les quittances déjà existantes ne sont jamais recréées.</p>
          <label className="block">
            <span className="block text-sm font-medium text-brand-green/70 mb-1.5">Bien concerné</span>
            <select value={property} onChange={(e) => setProperty(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-brand-soft outline-none text-sm focus:border-brand-limeDark bg-white" data-testid="gen-property-select">
              <option value="">Tous les biens occupés</option>
              {properties.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
          </label>
          <div className="flex gap-2 p-1 bg-brand-softer rounded-full">
            <button onClick={() => setMode("single")} className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${mode === "single" ? "bg-white shadow text-brand-greenDeep" : "text-brand-green/60"}`} data-testid="gen-mode-single">Un mois</button>
            <button onClick={() => setMode("range")} className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${mode === "range" ? "bg-white shadow text-brand-greenDeep" : "text-brand-green/60"}`} data-testid="gen-mode-range">Une période</button>
          </div>
          <div className={`grid ${mode === "range" ? "grid-cols-2" : "grid-cols-1"} gap-3`}>
            <label className="block">
              <span className="block text-sm font-medium text-brand-green/70 mb-1.5">{mode === "range" ? "Du mois" : "Mois"}</span>
              <input type="month" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-brand-soft outline-none text-sm focus:border-brand-limeDark" data-testid="gen-from-input" />
            </label>
            {mode === "range" && (
              <label className="block">
                <span className="block text-sm font-medium text-brand-green/70 mb-1.5">Au mois</span>
                <input type="month" value={to} onChange={(e) => setTo(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-brand-soft outline-none text-sm focus:border-brand-limeDark" data-testid="gen-to-input" />
              </label>
            )}
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} disabled={busy} className="flex-1 py-3 rounded-full border border-brand-soft text-brand-greenDeep font-semibold hover:bg-brand-softer transition-colors disabled:opacity-60" data-testid="gen-cancel">Annuler</button>
            <button onClick={run} disabled={busy} className="flex-1 py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60" data-testid="gen-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />} Générer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function RelanceModal({ receipt, onClose, onSend }) {
  const [busy, setBusy] = useState(0); // niveau en cours d'envoi (0 = aucun)
  const LEVELS = [
    { level: 1, title: "1er rappel", desc: "Courrier amiable invitant le locataire à régulariser sans délai.", cls: "bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep" },
    { level: 2, title: "2e relance", desc: "Relance formelle demandant la régularisation sous 15 jours.", cls: "bg-amber-100 hover:bg-amber-200 text-amber-800" },
    { level: 3, title: "Mise en demeure", desc: "Courrier juridique (art. 1344 C. civ.) : régularisation sous 8 jours avant procédure.", cls: "bg-red-500 hover:bg-red-600 text-white" },
  ];
  const send = async (level) => {
    setBusy(level);
    try { await onSend(receipt, level); } catch { /* toast géré par le parent */ } finally { setBusy(0); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="bg-white rounded-3xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="relance-modal">
        <div className="p-6 pb-4 border-b border-brand-soft flex items-center gap-3">
          <span className="w-11 h-11 rounded-full bg-amber-50 flex items-center justify-center shrink-0"><Bell className="w-5 h-5 text-amber-600" /></span>
          <div>
            <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Envoyer une relance</h3>
            <p className="text-xs text-brand-green/50">{receipt.tenant} · {receipt.property} · {receipt.month}</p>
          </div>
        </div>
        <div className="p-6 pt-5 space-y-3">
          <p className="text-sm text-brand-green/60">Un courrier de relance PDF est envoyé par e-mail au locataire. Choisissez le niveau adapté à la situation.</p>
          {LEVELS.map((l) => (
            <button key={l.level} onClick={() => send(l.level)} disabled={!!busy} data-testid={`relance-level-${l.level}`} className={`w-full text-left rounded-2xl p-4 transition-colors disabled:opacity-60 ${l.cls}`}>
              <span className="flex items-center justify-between font-semibold text-sm">{l.title} {busy === l.level && <Loader2 className="w-4 h-4 animate-spin" />}</span>
              <span className="block text-xs opacity-80 mt-1">{l.desc}</span>
            </button>
          ))}
          <button onClick={onClose} disabled={!!busy} className="w-full py-3 rounded-full border border-brand-soft text-brand-greenDeep font-semibold hover:bg-brand-softer transition-colors disabled:opacity-60" data-testid="relance-cancel">Annuler</button>
        </div>
      </div>
    </div>
  );
}
